import { h } from 'preact';
import { useState, useRef, useCallback, useEffect } from 'preact/hooks';
import { debounce } from '../debounce';

export function NumberFieldEntry({
  id,
  label,
  value,
  onChange,
  disabled = false,
  min,
  max,
  step = 1,
  validate,
  description,
  debounce: debounceOpt = 300,
}) {
  const [error, setError] = useState(null);
  const debouncedRef = useRef(null);

  const handleChange = useCallback(
    (val) => {
      const num = val === '' ? '' : Number(val);

      // Built-in range validation
      if (num !== '' && !isNaN(num)) {
        if (min !== undefined && num < min) {
          setError(`Minimum value is ${min}`);
          return;
        }
        if (max !== undefined && num > max) {
          setError(`Maximum value is ${max}`);
          return;
        }
      }

      // Custom validation
      if (validate) {
        const err = validate(num);
        setError(err || null);
        if (err) return;
      }

      setError(null);
      onChange(num);
    },
    [onChange, validate, min, max],
  );

  useEffect(() => {
    if (debounceOpt) {
      const delay = typeof debounceOpt === 'number' ? debounceOpt : 300;
      debouncedRef.current = debounce(handleChange, delay);
      return () => debouncedRef.current.cancel();
    }
    debouncedRef.current = null;
  }, [debounceOpt, handleChange]);

  const onInput = (e) => {
    const val = e.target.value;
    if (debouncedRef.current) {
      debouncedRef.current(val);
    } else {
      handleChange(val);
    }
  };

  return (
    <div class={`epx-entry epx-number-field${error ? ' has-error' : ''}`}>
      <label class="epx-entry-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        class="epx-entry-input"
        value={value != null ? value : ''}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onInput={onInput}
      />
      {error && <div class="epx-entry-error">{error}</div>}
      {description && <div class="epx-entry-description">{description}</div>}
    </div>
  );
}
